import { useCallback, useEffect, useState } from 'react';
import { Activity, ArrowUpRight, CircleAlert, Database, Mail, RefreshCw, ShieldCheck } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../api';
import type { OverviewData } from '../types';
import { formatTime, PageHeader } from '../components/Common';

/**
 * 功能：渲染总邮箱池统计、平台独立使用量和运行事件概览。
 * 参数：notify（全局通知回调）。
 */
export function OverviewScreen({ notify }: { notify: (message: string, type?: 'success' | 'error') => void }) {
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);

  /** 功能：从后端刷新概览统计；参数：silent（是否不显示成功通知）。 */
  const load = useCallback(async (silent = true): Promise<void> => {
    setLoading(true);
    try {
      const overview = await api<OverviewData>('/api/overview');
      setData(overview);
      if (!silent) notify('概览数据已刷新');
    } catch (error) {
      notify(error instanceof Error ? error.message : '概览加载失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  const metrics = [
    { label: '总邮箱数', value: data?.total ?? 0, icon: Database, note: '唯一邮箱资产池' },
    { label: '总已用邮箱', value: data?.used ?? 0, icon: Mail, note: '至少命中一个平台' },
    { label: '总未用邮箱', value: data?.unused ?? 0, icon: ShieldCheck, note: '尚未注册任何平台' },
    { label: '损坏邮箱', value: data?.damaged ?? 0, icon: CircleAlert, note: '命中损坏规则' },
  ];

  return (
    <div className="page overview-page">
      <PageHeader
        eyebrow="POOL OVERVIEW / LIVE DATA"
        title="邮箱资源概览"
        description="总池只计算唯一邮箱；每个平台独立复用并以总邮箱数为统计基线。"
        actions={<button className="button button-secondary" onClick={() => void load(false)} disabled={loading}><RefreshCw size={16} className={loading ? 'spin' : ''} />刷新</button>}
      />

      <section className="metric-grid" aria-label="邮箱关键指标">
        {metrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <article className="metric-cell" key={metric.label}>
              <div className="metric-meta"><span>0{index + 1}</span><Icon size={17} /></div>
              <strong>{loading && !data ? '—' : metric.value.toLocaleString()}</strong>
              <div className="metric-label">{metric.label}</div>
              <p>{metric.note}</p>
            </article>
          );
        })}
      </section>

      <section className="overview-grid">
        <div className="chart-section">
          <div className="section-heading">
            <div><span className="section-kicker">PLATFORM UTILIZATION</span><h2>平台邮箱使用</h2></div>
            <span className="freshness">更新 {formatTime(data?.updatedAt)}</span>
          </div>
          <div className="chart-scroll" tabIndex={0} aria-label="前十四个种类使用图，可横向滚动"><div className="chart-wrap">
            {data?.platforms.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.platforms.slice(0, 14)} margin={{ top: 12, right: 20, left: 0, bottom: 12 }} barCategoryGap="32%">
                  <CartesianGrid stroke="#e6e6e1" vertical={false} />
                  <XAxis dataKey="name" type="category" scale="band" interval={0} tickLine={false} axisLine={false} tickMargin={10} angle={-40} textAnchor="end" height={108} tick={{ fill: '#666661', fontSize: 11 }} tickFormatter={(name: string) => name.length > 18 ? `${name.slice(0, 17)}…` : name} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fill: '#888883', fontSize: 11 }} />
                  <Tooltip cursor={{ fill: '#f0f0ed' }} contentStyle={{ border: '1px solid #d9d9d4', borderRadius: 2, boxShadow: 'none' }} />
                  <Bar dataKey="used" name="已用" stackId="mailPool" fill="#11110f" maxBarSize={54} />
                  <Bar dataKey="unused" name="未用" stackId="mailPool" fill="#c9c9c4" radius={[2, 2, 0, 0]} maxBarSize={54} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="empty-state"><Activity size={24} /><strong>等待平台数据</strong><span>平台建立后会在这里独立统计邮箱使用量。</span></div>}
          </div></div>
          <div className="chart-legend"><span><i className="legend-dark" />已用</span><span><i />未用</span><em>现有排序前 14 个种类 · 单位：邮箱</em></div>
        </div>

        <div className="platform-ledger">
          <div className="section-heading"><div><span className="section-kicker">REUSABLE POOL</span><h2>平台账本</h2></div></div>
          <div className="ledger-scroll" tabIndex={0} role="region" aria-label="全部种类平台账本">
          <div className="ledger-head"><span>种类</span><span>已用 / 总数</span><span>剩余</span></div>
          {(data?.platforms || []).map((platform) => {
            const percentage = platform.total ? Math.round((platform.used / platform.total) * 100) : 0;
            return (
              <div className="ledger-row" key={platform.slug}>
                <div className="ledger-name"><span className="platform-code">{platform.name.slice(0, 2).toUpperCase()}</span><div><strong title={platform.name}>{platform.name}</strong><span title={platform.slug}>{platform.slug}</span></div></div>
                <div className="ledger-progress"><div><span style={{ width: `${percentage}%` }} /></div><small>{platform.used.toLocaleString()} / {platform.total.toLocaleString()}</small></div>
                <strong className="ledger-unused">{platform.unused.toLocaleString()}</strong>
              </div>
            );
          })}
          {!data?.platforms.length ? <div className="ledger-empty">暂无启用平台</div> : null}
          </div>
          <div className="ledger-note"><ArrowUpRight size={15} /><span>新增平台后无需改动邮箱表结构，统计会自动纳入。</span></div>
        </div>
      </section>

      <section className="technical-band">
        <div className="band-intro"><span>OPERATION TRACE</span><h2>最近运行事件</h2><p>导入、租约、注册上报和邮箱自检均写入同一审计序列。</p></div>
        <div className="event-list">
          {(data?.recentEvents || []).map((event) => (
            <div className="event-row" key={event.id}>
              <span className={`event-signal signal-${event.level}`} />
              <code>#{String(event.id).padStart(4, '0')}</code>
              <div><strong>{event.message}</strong><span>{event.type}</span></div>
              <time>{formatTime(event.createdAt)}</time>
            </div>
          ))}
          {!data?.recentEvents.length ? <div className="event-empty"><Activity size={20} /><span>运行事件将在首次导入邮箱后出现</span></div> : null}
        </div>
      </section>
    </div>
  );
}
